import * as React from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  BadgeCheck,
  CheckCircle2,
  Clock,
  Eye,
  Inbox,
  ShieldCheck,
  ShieldX,
  Store,
  XCircle,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Badge,
  Card,
  CardContent,
  EmptyState,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/primitives";
import { PageHeader, SearchInput, Toolbar } from "@/components/data/toolbar";
import { ConfirmDialog } from "@/components/data/ConfirmDialog";
import { DocumentViewer } from "./DocumentViewer";

interface RequestRow {
  id: string;
  breeder_id: string;
  name: string;
  phone: string;
  email: string | null;
  city: string | null;
  status: string;
  breeder_verified: boolean;
  breeder_is_approved: boolean;
  breeder_status: string;
  doc_count: number;
  documents: any[];
  submitted_at: string;
  reviewed_at: string | null;
  reviewer_name: string | null;
  admin_notes: string | null;
  rejection_reason: string | null;
}

type StatusTab = "pending" | "verified" | "approved_unverified" | "rejected";

export const VerificationPage = () => {
  const queryClient = useQueryClient();

  const [tab, setTab] = React.useState<StatusTab>("pending");
  const [q, setQ] = React.useState("");
  const [openId, setOpenId] = React.useState<string | null>(null);
  const [decision, setDecision] = React.useState<{
    id: string;
    breederId?: string;
    name: string;
    kind: "approve_verify" | "approve_only" | "reject" | "revoke";
  } | null>(null);

  const { data: counts } = useQuery({
    queryKey: ["verification", "counts"],
    queryFn: () =>
      api.get<{
        pending: number;
        approved: number;
        rejected: number;
        verified: number;
        approved_unverified: number;
        oldest_pending_at: string | null;
        oldest_pending_age_hours: number | null;
      }>("/verification/counts"),
    refetchInterval: 15_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["verification", tab, q],
    queryFn: () =>
      api.get<{ items: RequestRow[]; total: number }>("/verification", {
        status: tab,
        q: q || undefined,
        limit: 50,
      }),
  });

  const decide = useMutation({
    mutationFn: ({
      id,
      kind,
      reason,
    }: {
      id: string;
      kind: "approve_verify" | "approve_only" | "reject" | "revoke";
      reason?: string;
    }) => {
      if (kind === "approve_verify") {
        return api.postWithMessage(`/verification/${id}/approve`, {
          grant_badge: true,
          notes: reason || "Identity documents approved. Verified badge granted.",
        });
      }
      if (kind === "approve_only") {
        return api.postWithMessage(`/verification/${id}/approve`, {
          grant_badge: false,
          notes: reason || "Breeder profile approved for listing without verified badge.",
        });
      }
      if (kind === "revoke") {
        return api.postWithMessage(`/verification/${id}/revoke`, {});
      }
      return api.postWithMessage(`/verification/${id}/reject`, { reason });
    },
    onSuccess: ({ message }) => {
      toast.success(message);
      setDecision(null);
      setOpenId(null);
      void queryClient.invalidateQueries({ queryKey: ["verification"] });
      void queryClient.invalidateQueries({ queryKey: ["breeders"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard", "kpis"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Could not save the decision"),
  });

  const rows = data?.items ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Breeder Verification & Approval Hub"
        description="Review submitted KYC documents, pet shop licenses, and manage Verified Breeder trust badges vs account selling approvals."
      />

      {/* Summary KPI Bar */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card
          className={`cursor-pointer transition-all ${
            tab === "pending" ? "border-amber-500 bg-amber-500/5 shadow-sm" : "border-border hover:border-border/80"
          }`}
          onClick={() => setTab("pending")}
        >
          <CardContent className="flex items-center gap-3 p-4">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-amber-500/10 text-amber-500">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Pending KYC</p>
              <p className="text-xl font-bold text-foreground">{counts?.pending ?? 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all ${
            tab === "verified" ? "border-emerald-500 bg-emerald-500/5 shadow-sm" : "border-border hover:border-border/80"
          }`}
          onClick={() => setTab("verified")}
        >
          <CardContent className="flex items-center gap-3 p-4">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-emerald-500/10 text-emerald-500">
              <BadgeCheck className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Verified Breeders</p>
              <p className="text-xl font-bold text-foreground">{counts?.verified ?? 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all ${
            tab === "approved_unverified"
              ? "border-sky-500 bg-sky-500/5 shadow-sm"
              : "border-border hover:border-border/80"
          }`}
          onClick={() => setTab("approved_unverified")}
        >
          <CardContent className="flex items-center gap-3 p-4">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-sky-500/10 text-sky-500">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Approved (No Badge)</p>
              <p className="text-xl font-bold text-foreground">{counts?.approved_unverified ?? 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all ${
            tab === "rejected" ? "border-rose-500 bg-rose-500/5 shadow-sm" : "border-border hover:border-border/80"
          }`}
          onClick={() => setTab("rejected")}
        >
          <CardContent className="flex items-center gap-3 p-4">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-rose-500/10 text-rose-500">
              <ShieldX className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Rejected</p>
              <p className="text-xl font-bold text-foreground">{counts?.rejected ?? 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as StatusTab)}>
        <TabsList>
          <TabsTrigger value="pending" className="gap-2">
            Pending KYC Queue
            <Badge variant="warning" className="rounded-full px-1.5 py-0 text-[10px]">
              {counts?.pending ?? 0}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="verified" className="gap-2">
            Verified Breeders
            <Badge variant="success" className="rounded-full px-1.5 py-0 text-[10px]">
              {counts?.verified ?? 0}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="approved_unverified" className="gap-2">
            Approved (Unverified)
            <Badge variant="secondary" className="rounded-full px-1.5 py-0 text-[10px]">
              {counts?.approved_unverified ?? 0}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="rejected" className="gap-2">
            Rejected
            <Badge variant="secondary" className="rounded-full px-1.5 py-0 text-[10px]">
              {counts?.rejected ?? 0}
            </Badge>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <Toolbar>
        <SearchInput
          value={q}
          onChange={setQ}
          placeholder="Search by breeder name, city, phone..."
        />
      </Toolbar>

      {/* Verification Rows */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full rounded-xl" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <Card className="p-8">
          <EmptyState
            icon={Inbox}
            title={
              tab === "pending"
                ? "No pending verification requests"
                : `No requests found in this section`
            }
            description={
              tab === "pending"
                ? "All breeder verification submissions have been reviewed!"
                : undefined
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <Card key={row.id} className="border-border hover:border-primary/40 transition-colors">
              <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-4">
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                    <Store className="h-6 w-6" />
                  </span>

                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/breeders/${row.breeder_id}`}
                        className="font-bold text-foreground hover:underline"
                      >
                        {row.name}
                      </Link>

                      {row.breeder_verified ? (
                        <Badge variant="success" className="gap-1">
                          <BadgeCheck className="h-3 w-3" /> Verified Badge
                        </Badge>
                      ) : row.breeder_is_approved ? (
                        <Badge variant="default" className="gap-1">
                          <ShieldCheck className="h-3 w-3" /> Approved Seller
                        </Badge>
                      ) : row.status === "rejected" ? (
                        <Badge variant="destructive">Rejected</Badge>
                      ) : (
                        <Badge variant="warning">KYC Pending Review</Badge>
                      )}
                    </div>

                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {[
                        row.phone ? `+91 ${row.phone}` : null,
                        row.email,
                        row.city,
                        `${row.doc_count} document${row.doc_count === 1 ? "" : "s"} attached`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>

                    <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground/80">
                      <Clock className="h-3 w-3" /> Submitted {formatDateTime(row.submitted_at)}
                    </p>

                    {row.admin_notes && (
                      <p className="mt-1 text-xs text-muted-foreground">Notes: {row.admin_notes}</p>
                    )}
                    {row.rejection_reason && (
                      <p className="mt-1 text-xs text-destructive">Reason: {row.rejection_reason}</p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-2 self-end sm:self-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOpenId(row.id)}
                    className="gap-1.5"
                  >
                    <Eye className="h-4 w-4" /> View Documents
                  </Button>

                  {/* Pending KYC Queue Actions */}
                  {row.status === "pending" && (
                    <>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() =>
                          setDecision({
                            id: row.id,
                            breederId: row.breeder_id,
                            name: row.name,
                            kind: "approve_verify",
                          })
                        }
                        className="gap-1.5"
                      >
                        <BadgeCheck className="h-4 w-4" /> Approve &amp; Verify
                      </Button>

                      <Button
                        variant="default"
                        size="sm"
                        onClick={() =>
                          setDecision({
                            id: row.id,
                            breederId: row.breeder_id,
                            name: row.name,
                            kind: "approve_only",
                          })
                        }
                        className="gap-1.5"
                      >
                        <CheckCircle2 className="h-4 w-4" /> Approve Profile Only
                      </Button>

                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() =>
                          setDecision({
                            id: row.id,
                            breederId: row.breeder_id,
                            name: row.name,
                            kind: "reject",
                          })
                        }
                        className="gap-1.5"
                      >
                        <XCircle className="h-4 w-4" /> Reject KYC
                      </Button>
                    </>
                  )}

                  {/* Verified Breeders Tab Actions */}
                  {tab === "verified" && row.breeder_verified && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setDecision({
                          id: row.id,
                          breederId: row.breeder_id,
                          name: row.name,
                          kind: "revoke",
                        })
                      }
                      className="gap-1.5 text-destructive hover:bg-destructive/10"
                    >
                      <XCircle className="h-4 w-4" /> Revoke Badge
                    </Button>
                  )}

                  {/* Approved Unverified Tab Actions */}
                  {tab === "approved_unverified" && !row.breeder_verified && (
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() =>
                        setDecision({
                          id: row.id,
                          breederId: row.breeder_id,
                          name: row.name,
                          kind: "approve_verify",
                        })
                      }
                      className="gap-1.5"
                    >
                      <BadgeCheck className="h-4 w-4" /> Grant Verified Badge
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Document Viewer Modal */}
      {openId && (
        <DocumentViewer
          requestId={openId}
          open={Boolean(openId)}
          onOpenChange={(open) => !open && setOpenId(null)}
          onApproveVerify={(id) => {
            const r = rows.find((x) => x.id === id);
            setDecision({
              id,
              breederId: r?.breeder_id,
              name: r?.name ?? "this breeder",
              kind: "approve_verify",
            });
          }}
          onApproveOnly={(id) => {
            const r = rows.find((x) => x.id === id);
            setDecision({
              id,
              breederId: r?.breeder_id,
              name: r?.name ?? "this breeder",
              kind: "approve_only",
            });
          }}
          onReject={(id) => {
            const r = rows.find((x) => x.id === id);
            setDecision({
              id,
              breederId: r?.breeder_id,
              name: r?.name ?? "this breeder",
              kind: "reject",
            });
          }}
        />
      )}

      {/* Approve & Verify Confirm Dialog */}
      <ConfirmDialog
        open={decision?.kind === "approve_verify"}
        onOpenChange={(open) => !open && setDecision(null)}
        title={`Approve & Verify ${decision?.name}?`}
        description="Approving KYC documents grants the official Verified Breeder blue badge and enables verified seller status across all listings."
        confirmLabel="Approve & Grant Badge"
        variant="default"
        loading={decide.isPending}
        onConfirm={(notes) =>
          decision &&
          decide.mutate({ id: decision.id, kind: "approve_verify", reason: notes || "" })
        }
      />

      {/* Approve Profile Only Confirm Dialog */}
      <ConfirmDialog
        open={decision?.kind === "approve_only"}
        onOpenChange={(open) => !open && setDecision(null)}
        title={`Approve Profile Only for ${decision?.name}?`}
        description="This will allow the breeder to post pet listings, but will NOT grant the blue Verified Breeder trust badge."
        confirmLabel="Approve Profile Only"
        variant="default"
        loading={decide.isPending}
        onConfirm={(notes) =>
          decision &&
          decide.mutate({ id: decision.id, kind: "approve_only", reason: notes || "" })
        }
      />

      {/* Revoke Badge Confirm Dialog */}
      <ConfirmDialog
        open={decision?.kind === "revoke"}
        onOpenChange={(open) => !open && setDecision(null)}
        title={`Revoke Verified Badge from ${decision?.name}?`}
        description="This will remove the blue Verified Breeder badge from their profile and listings. The breeder account will remain active."
        confirmLabel="Revoke Verified Badge"
        variant="destructive"
        loading={decide.isPending}
        onConfirm={() =>
          decision && decide.mutate({ id: decision.id, kind: "revoke" })
        }
      />

      {/* Reject Confirm Dialog */}
      <ConfirmDialog
        open={decision?.kind === "reject"}
        onOpenChange={(open) => !open && setDecision(null)}
        title={`Reject KYC Documents for ${decision?.name}?`}
        description="Provide a clear rejection reason explaining what documents or requirements are missing."
        confirmLabel="Reject KYC Request"
        variant="destructive"
        requireReason
        reasonLabel="Rejection reason"
        reasonHint="Explain what is invalid or missing (e.g. Blurry ID, Expired Pet Shop License)."
        loading={decide.isPending}
        onConfirm={(reason) =>
          decision && decide.mutate({ id: decision.id, kind: "reject", reason })
        }
      />
    </div>
  );
};

